import React, { useEffect, useState } from 'react';
import { useReducedMotion } from '../../hooks/useReducedMotion';

/**
 * Reveal — a lightweight "fade + rise in" wrapper used to choreograph a page's
 * entrance. Pass an `index` to stagger siblings (each ~60ms later). When the
 * user prefers reduced motion, it renders fully visible with no transition.
 *
 * Pure CSS transforms — no animation library.
 */
export function Reveal({
  index = 0,
  children,
  as = 'div',
  style,
  stagger = 60,
  distance = 14,
  duration = 480,
}: {
  index?: number;
  children: React.ReactNode;
  as?: 'div' | 'section' | 'li';
  style?: React.CSSProperties;
  stagger?: number;
  distance?: number;
  duration?: number;
}) {
  const reduced = useReducedMotion();
  const [shown, setShown] = useState(reduced);

  useEffect(() => {
    if (reduced) { setShown(true); return; }
    const t = setTimeout(() => setShown(true), index * stagger + 20);
    return () => clearTimeout(t);
  }, [index, stagger, reduced]);

  const Tag = as as any;
  const motionStyle: React.CSSProperties = reduced
    ? {}
    : {
        opacity: shown ? 1 : 0,
        transform: shown ? 'translateY(0)' : `translateY(${distance}px)`,
        transition: `opacity ${duration}ms cubic-bezier(.2,.8,.2,1), transform ${duration}ms cubic-bezier(.2,.8,.2,1)`,
        willChange: 'opacity, transform',
      };

  return <Tag style={{ ...style, ...motionStyle }}>{children}</Tag>;
}
